<!--макет кнопок-->
<!--https://www.figma.com/design/tfJqdNMzBGCZLBn76G2Bym/Expression-DS---Core-Components?m=auto&node-id=0-1&t=L3djVWOJOqDTB1ZR-1-->

<script setup lang="ts">
import IconLoadingBlack from './IconLoadingBlack.vue';
import IconLoadingWhite from './IconLoadingWhite.vue';

import { EButtonSize } from '../models/size';
import { EButtonType } from '../models/type';
import { computed, useAttrs } from 'vue';

const props = withDefaults(
  defineProps<{
    type?: EButtonType;
    size?: EButtonSize;
    w100?: boolean;
    loading?: boolean;
    disabled?: boolean;
    round?: boolean;
  }>(),
  {
    type: EButtonType.primaryAccented,
    size: EButtonSize.M40,
  }
);

const styleObj = computed(() => {
  const style = {} as any;

  style.width = props.w100 ? '100%' : 'fit-content';

  return style;
});

const WHITE_ICON_TYPES = [
  EButtonType.primaryAccented,
  EButtonType.primaryNeutral,
  EButtonType.quaternaryAccented,
  EButtonType.quaternaryNeutral,
  EButtonType.quaternaryDark,
];
const iconLoading = computed(() => {
  return WHITE_ICON_TYPES.includes(props.type) ? IconLoadingWhite : IconLoadingBlack;
});

const $attrs = useAttrs();
const buttonComponent = computed(() => {
  if ($attrs.href) return 'a';
  if ($attrs.to) return 'router-link';

  return 'button';
});
</script>

<template>
  <component
    :is="buttonComponent"
    :to="$attrs.to"
    :class="[
      'button',
      `button_${type}`,
      `button_${size}`,
      { button_round: round },
      { button_loading: loading },
      { button_disabled: disabled },
    ]"
    :style="styleObj"
  >
    <component :is="iconLoading" v-if="loading" class="button__icon-loading" />
    <div class="button__content" :style="{ opacity: loading ? 0 : 1 }">
      <slot />
    </div>
  </component>
</template>

<style lang="stylus" scoped>
.button__content
  display flex
  align-items center
  gap: 4px

.button
  position relative
  font-family Inter
  outline none
  text-decoration none
  box-shadow none
  display flex
  cursor pointer
  align-items center
  transition-duration .2s
  text-align center
  justify-content center
  border-color transparent

    &:hover
      background-color var(--button-disabled-onprimary)

  &_loading {
    pointer-events none
  }

  &__icon-loading
    position absolute

  // type

  &_primary-accented
    background-color var(--button-primary-accented-rest)
    color white

    &:hover
      background-color var(--button-primary-accented-hovered)

  &_primary-neutral
    background-color var(--button-primary-neutral-rest)
    color white

    &:hover
      background-color var(--button-primary-neutral-hovered)

  &_secondary-accented
    background-color var(--button-secondary-accented-rest)
    color var(--text-accented)

    &:hover
      background-color var(--button-secondary-accented-hovered)

  &_secondary-neutral
    background-color var(--button-secondary-neutral-onprimary-rest)
    color rgba(31, 32, 38, 1)

    &:hover
      background-color var(--button-secondary-neutral-onprimary-hovered)

  &_tertiary-outlined
    background-color white
    color var(--text-primary)
    border solid 1px var(--border-regular)

    &:hover
      background-color var(--button-secondary-neutral-onprimary-hovered)

  &_tertiary-ghost
    background-color white
    color var(--text-primary)
    border solid 1px white

    &:hover
      border solid 1px var(--button-tertiary-neutral-onprimary-hovered)
      background-color var(--button-tertiary-neutral-onprimary-hovered)

  &_quaternary-accented
    border solid 1px #EA3324
    background-color #EA3324
    color white

    &:hover
      border solid 1px #F54E40
      background-color #F54E40

    &.button_disabled
      color white
      border solid 1px #F7B5B0
      background-color #F7B5B0

  &_quaternary-neutral
    color white
    border solid 1px #1D1346
    background-color #1D1346

    &:hover
      border solid 1px #312270
      background-color #312270

    &.button_disabled
      color white
      border solid 1px #B4AFC9
      background-color #B4AFC9

  &_quaternary-dark
    border solid 1px #141414
    background-color #141414;
    color #ffffff

    &:hover
      border solid 1px #242424
      background-color #242424

    &.button_disabled
      color white
      background-color #B8B8B8
      border solid 1px #B8B8B8


  // size

  &_xs28
    font-weight 500
    font-size 14px
    padding 0 12px
    height 28px
    border-radius 8px

    .button__icon-loading
      height 16px
      width 16px

  &_xs32
    font-weight 500
    font-size 14px
    padding 0 12px
    height 32px
    border-radius 8px

    .button__icon-loading
      height 16px
      width 16px

  &_s36
    font-weight 500
    font-size 14px
    padding 0 14px
    height 36px
    border-radius 8px

    .button__icon-loading
      height 16px
      width 16px

  &_m40
    font-weight 500
    font-size 14px
    padding 0 16px
    height 40px
    border-radius 10px

    .button__icon-loading
      height 20px
      width 20px

  &_l48
    font-weight 500
    font-size 16px
    padding 0 20px
    height 48px
    border-radius 12px

    .button__icon-loading
      height 24px
      width 24px

  &_xl56
    font-weight 600
    font-size 16px
    padding 0 24px
    height 56px
    border-radius 12px

    .button__icon-loading
      height 24px
      width 24px

  &_round
    border-radius 50%
    aspect-ratio 1

  &_disabled
    pointer-events none
    background-color: var(--button-disabled-onprimary)
    color: var(--text-disabled)
</style>
